const DEFAULT_ONBOARD_CREDIT_NOTE = '선내에서 사용 가능한 무료 리워드 크레딧';

const parseAmount = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const normalized = typeof value === 'string' ? value.replace(/,/g, '').trim() : value;
  if (normalized === '') return null;
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const isOnboardCreditEntry = (categoryRaw = '', descriptionRaw = '') => {
  const normalized = `${categoryRaw} ${descriptionRaw}`
    .toLowerCase()
    .replace(/[^a-z]/g, '');
  return normalized.includes('onboardcredit');
};

const appendItem = (itemMap, descriptionRaw, amountRaw, currencyRaw = 'USD') => {
  const amount = parseAmount(amountRaw);
  if (!(amount > 0)) return;

  const description = String(descriptionRaw || 'On-Board Credit').trim() || 'On-Board Credit';
  const currency = String(currencyRaw || 'USD').trim().toUpperCase() || 'USD';
  const key = `${description.toLowerCase()}|${currency}`;
  const existing = itemMap.get(key);

  if (existing) {
    existing.amount += amount;
    return;
  }

  itemMap.set(key, {
    description,
    amount,
    currency,
  });
};

const formatCurrency = (amount, currency = 'USD') => {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return '';
  return numeric.toLocaleString('en-US', { style: 'currency', currency });
};

export const normalizeOnboardCreditSummary = (summary, fallbackCurrency = 'USD') => {
  if (!summary || typeof summary !== 'object') return null;
  const itemsRaw = Array.isArray(summary.items) ? summary.items : [];
  if (itemsRaw.length === 0) return null;

  const items = itemsRaw
    .map((item) => {
      const amount = parseAmount(item?.amount ?? item?.total_amount ?? null);
      if (!(amount > 0)) return null;
      const currency = String(item?.currency || summary?.currency || fallbackCurrency || 'USD').trim().toUpperCase() || 'USD';
      return {
        description: String(item?.description || 'On-Board Credit').trim() || 'On-Board Credit',
        amount,
        currency,
        amountLabel: item?.amount_label || formatCurrency(amount, currency),
        guestNo: Number(item?.guest_no) > 0 ? Number(item.guest_no) : null,
        lastName: String(item?.last_name || '').trim(),
        firstName: String(item?.first_name || '').trim(),
      };
    })
    .filter(Boolean);

  if (items.length === 0) return null;

  const totalAmount = parseAmount(summary.total_amount) ?? items.reduce((sum, item) => sum + item.amount, 0);
  const currency = String(summary.currency || items[0]?.currency || fallbackCurrency || 'USD').trim().toUpperCase() || 'USD';

  return {
    title: String(summary.title || '특별 혜택'),
    note: String(summary.note || DEFAULT_ONBOARD_CREDIT_NOTE),
    currency,
    totalAmount,
    totalAmountLabel: summary.total_amount_label || formatCurrency(totalAmount, currency),
    items,
  };
};

export const extractOnboardCreditSummaryFromSource = (source, fallbackCurrency = 'USD') => {
  if (!source || typeof source !== 'object') return null;

  const itemMap = new Map();
  const onboardCurrency = String(source.obccurrency || source.currency || fallbackCurrency || 'USD').trim().toUpperCase() || 'USD';

  const perperson = Array.isArray(source.perperson) ? source.perperson : [];
  if (perperson.length > 0) {
    perperson.forEach((category) => {
      if (!isOnboardCreditEntry(category?.category, category?.description)) return;
      const prices = Array.isArray(category?.prices) ? category.prices : [];
      prices.forEach((priceRow) => {
        appendItem(
          itemMap,
          category?.description || category?.category || 'On-Board Credit',
          priceRow?.price ?? priceRow?.sprice,
          priceRow?.currency || priceRow?.scurrency || onboardCurrency,
        );
      });
    });
  }

  if (itemMap.size === 0) {
    const breakdown = Array.isArray(source.breakdown) ? source.breakdown : [];
    breakdown.forEach((row) => {
      if (!isOnboardCreditEntry(row?.category, row?.description)) return;
      appendItem(
        itemMap,
        row?.description || row?.category || 'On-Board Credit',
        row?.itemprice ?? row?.totalcost ?? row?.price ?? row?.sprice,
        row?.currency || row?.scurrency || onboardCurrency,
      );
    });
  }

  if (itemMap.size === 0) {
    appendItem(itemMap, 'On-Board Credit', source.onboardcredit, onboardCurrency);
  }

  if (itemMap.size === 0) return null;

  const items = Array.from(itemMap.values())
    .sort((a, b) => a.description.localeCompare(b.description))
    .map((item) => ({
      ...item,
      amountLabel: formatCurrency(item.amount, item.currency),
    }));
  const totalAmount = items.reduce((sum, item) => sum + item.amount, 0);
  const currency = items[0]?.currency || onboardCurrency;

  return {
    title: '특별 혜택',
    note: DEFAULT_ONBOARD_CREDIT_NOTE,
    currency,
    totalAmount,
    totalAmountLabel: formatCurrency(totalAmount, currency),
    items,
  };
};
